import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { US_STATES } from "@/lib/constants";

interface University {
  id?: string;
  name: string;
  city: string;
  state: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
}

interface UniversityEditableCardProps {
  university: University;
  index: number;
  states: string[];
  onUpdate: (index: number, university: University) => void;
  onRemove: (index: number) => void;
}

export const UniversityEditableCard: React.FC<UniversityEditableCardProps> = ({
  university,
  index,
  states,
  onUpdate,
  onRemove
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedUni, setEditedUni] = useState(university);

  const handleSave = () => {
    onUpdate(index, editedUni);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedUni(university);
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <div className="p-4 border rounded-lg bg-background">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-1">
            <p className="font-medium">{university.name}</p>
            <p className="text-sm text-muted-foreground">{university.city}, {university.state}</p>
            {university.contact_person && (
              <div className="text-sm text-muted-foreground mt-2 pt-2 border-t">
                <p className="font-medium text-foreground mb-1">Coordinator:</p>
                <p>{university.contact_person}</p>
                {university.contact_email && <p>{university.contact_email}</p>}
                {university.contact_phone && <p>{university.contact_phone}</p>}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemove(index)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 border rounded-lg bg-background space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">University Name *</Label>
          <Input
            value={editedUni.name}
            onChange={(e) => setEditedUni({ ...editedUni, name: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-xs">City *</Label>
          <Input
            value={editedUni.city}
            onChange={(e) => setEditedUni({ ...editedUni, city: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-xs">State *</Label>
          <MultiSelectDropdown
            items={states.length > 0 ? states : US_STATES}
            selected={editedUni.state ? [editedUni.state] : []}
            onChange={(selectedStates) => setEditedUni({ ...editedUni, state: selectedStates[0] || '' })}
            placeholder="Select state..."
            searchPlaceholder="Search states..."
          />
        </div>
      </div>
      <div className="border-t pt-3">
        <p className="text-sm font-medium mb-2">Coordinator (Optional)</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={editedUni.contact_person || ''}
              onChange={(e) => setEditedUni({ ...editedUni, contact_person: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              value={editedUni.contact_email || ''}
              onChange={(e) => setEditedUni({ ...editedUni, contact_email: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input
              type="tel"
              value={editedUni.contact_phone || ''}
              onChange={(e) => setEditedUni({ ...editedUni, contact_phone: e.target.value })}
            />
          </div>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
